import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';

// Public Pages (always loaded - no auth required or part of core flow)
import { LoginComponent } from './pages/login/login.component';
import { RegistrationRedirectComponent } from './pages/registration-redirect/registration-redirect.component';
import { ImpressumComponent } from './pages/impressum/impressum.component';
import { TermsComponent } from './pages/terms/terms.component';
import { PrivacyComponent } from './pages/privacy/privacy.component';

const routes: Routes = [
  // ============================================
  // PUBLIC ROUTES (no lazy loading needed)
  // ============================================
  { path: 'login', component: LoginComponent, canActivate: [authGuard] },
  { path: 'impressum', component: ImpressumComponent },
  { path: 'terms', component: TermsComponent },
  { path: 'privacy', component: PrivacyComponent },

  // Registration lives in the standalone registration app (DOCU-639);
  // old links (incl. /registration/success from Stripe return URLs) get forwarded
  {
    path: 'registration',
    children: [
      { path: '', component: RegistrationRedirectComponent, pathMatch: 'full' },
      { path: '**', component: RegistrationRedirectComponent },
    ],
  },
  {
    path: 'registrierung',
    redirectTo: 'registration',
    pathMatch: 'prefix'
  },

  // ============================================
  // LAZY LOADED FEATURE MODULES
  // ============================================

  // Dashboard (lazy loaded for better initial load performance)
  {
    path: 'dashboard',
    loadChildren: () => import('./features/dashboard/dashboard.module').then(m => m.DashboardModule),
  },

  // Audits Feature - handles all audit-related routes
  {
    path: 'audits',
    loadChildren: () => import('./features/audits/audits.module').then(m => m.AuditsModule),
  },

  // User Feature - handles all user-related routes
  {
    path: 'user',
    loadChildren: () => import('./features/user/user.module').then(m => m.UserModule),
  },

  // Organisation Feature
  {
    path: 'organisation',
    loadChildren: () => import('./features/organisation/organisation.module').then(m => m.OrganisationModule),
  },

  // Templates Feature (DOCU-767) - single entry point for form templates:
  // list, detail, activation AND the editor (formerly the separate "Designer")
  {
    path: 'templates',
    loadChildren: () => import('./features/templates/templates.module').then(m => m.TemplatesModule),
  },

  // Export Templates Feature (DOCU-722) - org-scoped DOCX export template management
  {
    path: 'export-templates',
    loadChildren: () => import('./features/export-templates/export-templates.module').then(m => m.ExportTemplatesModule),
  },

  // Admin Templates Feature - for system admins to create global templates
  {
    path: 'admin-templates',
    loadChildren: () => import('./features/admin-templates/admin-templates.module').then(m => m.AdminTemplatesModule),
  },

  // ============================================
  // LEGACY ROUTES (backwards compatibility)
  // These routes maintain the old URL structure
  // ============================================
  // DOCU-767: Designer wurde in das Formulare-Feature integriert
  { path: 'designer/new', redirectTo: 'templates/new' },
  { path: 'designer/edit/:templateId', redirectTo: 'templates/edit/:templateId' },
  { path: 'designer', redirectTo: 'templates', pathMatch: 'full' },
  { path: 'form-builder', redirectTo: 'templates', pathMatch: 'full' },
  { path: 'user-management', redirectTo: 'user', pathMatch: 'full' },

  // ============================================
  // DEFAULT AND FALLBACK
  // ============================================
  { path: '', redirectTo: 'login', pathMatch: 'full' },
  { path: '**', redirectTo: 'login' },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      // Enable route preloading for better UX after initial load
      // Modules are loaded in background after initial navigation
      preloadingStrategy: PreloadAllModules,
      // Enable tracing for debugging (disable in production)
      // enableTracing: true,
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
